import React from 'react';
import Select, { components } from 'react-select';
import moment from 'moment';
import { withTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { mq, isMobile } from '@styles/breakpoints';
import { ThemeProps } from '@theme/base';
import { Icons } from '@model/common/icons';
import { Keyable, LONG_MONTH_YEAR_FORMAT, YEAR_MONTH_FORMAT } from '@model/common';
import { MAXIMUM_BOOKING_YEARS } from '@model/search';
import { Icon } from '@components/common/generic-icon';
import { TestId } from '@components/test-ids';
import { dropDownStyles } from './styles';

const Container = styled.div<any>(({ theme }: ThemeProps) => ({
  alignItems: 'center',
  display: 'flex',
  marginBottom: theme.custom.spacing.large,
  position: 'relative',
  width: '100%',
  ['> div']: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    justifyContent: 'center',
    [mq.small]: {
      width: '50%'
    }
  }
}));

interface StyledChevronProps extends ThemeProps {
  disabled: boolean;
  next?: boolean;
}
const StyledChevron = styled<any>(Icon)(({ theme, disabled, next }: StyledChevronProps) => ({
  fontSize: '1.4rem',
  color: theme.custom.colors.group4.base,
  cursor: 'pointer',
  padding: theme.custom.spacing.small,
  margin: -theme.custom.spacing.small,
  position: 'absolute',
  ...(!next && { left: 0 }),
  ...(next && { right: 0 }),
  ':hover': {
    color: theme.custom.colors.group7.base,
    ...(disabled && { color: theme.custom.colors.group4.lighter, cursor: 'default' })
  },
  ...(disabled && { color: theme.custom.colors.group4.lighter, cursor: 'default' })
}));

const StyledSelect = styled(Select)({
  minWidth: 160
});

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <Icon name={props.selectProps.menuIsOpen ? Icons.CARET_UP : Icons.CARET_DOWN} />
    </components.DropdownIndicator>
  );
};

export interface NavigationProps extends ThemeProps {
  month: moment.Moment;
  onPrev: () => void;
  onNext: () => void;
  onSelect: (value: Keyable | null, offset: number) => void;
}

export const Navigation = withTheme(({ month, onPrev, onNext, onSelect, theme }: NavigationProps) => {
  const canNavigateBackwards: boolean = month.clone().subtract(1, 'month').isSameOrAfter(moment(), 'month');
  const canNavigateForwards: boolean = month
    .clone()
    .add(isMobile() ? 1 : 2, 'month')
    .isSameOrBefore(moment().add(MAXIMUM_BOOKING_YEARS, 'years'), 'month');

  const selectedIso = month.format(YEAR_MONTH_FORMAT);
  const nextMonthIso = month.clone().add(1, 'month').format(YEAR_MONTH_FORMAT);

  const options = [...Array(MAXIMUM_BOOKING_YEARS * 12 + 1)].map((_, idx) => {
    const date = moment().add(idx, 'month');
    return {
      value: date.format(YEAR_MONTH_FORMAT),
      label: date.format(LONG_MONTH_YEAR_FORMAT)
    };
  });

  const currentOptions = options.slice(0, options.length - 1);
  const currentSelected = options.find(({ value }) => value === selectedIso);
  const nextOptions = options.slice(1);
  const nextSelected = nextOptions.find(({ value }) => value === nextMonthIso);

  return (
    <Container>
      <StyledChevron name={Icons.CHEVRON_LEFT} disabled={!canNavigateBackwards} onClick={onPrev} />
      <div data-testid={TestId.dealFinder.dayPicker.month}>
        <StyledSelect
          isSearchable={false}
          maxMenuHeight={280}
          value={currentSelected}
          onChange={(value) => onSelect(value, 0)}
          options={currentOptions}
          styles={dropDownStyles(theme)}
          components={{
            IndicatorSeparator: null,
            DropdownIndicator
          }}
        />
      </div>
      {!isMobile() && (
        <div>
          <StyledSelect
            isSearchable={false}
            data-testid={TestId.dealFinder.dayPicker.month}
            maxMenuHeight={280}
            value={nextSelected}
            onChange={(value) => onSelect(value, -1)}
            options={nextOptions}
            styles={dropDownStyles(theme)}
            components={{
              IndicatorSeparator: null,
              DropdownIndicator
            }}
          />
        </div>
      )}
      <StyledChevron name={Icons.CHEVRON_RIGHT} disabled={!canNavigateForwards} onClick={onNext} next />
    </Container>
  );
});
